import React, { useEffect } from 'react';
import { useLazyLoadQuery } from 'react-relay';
import { graphql } from 'relay-runtime';
import { toGlobalID } from 'src/shared/helpers';
import usePreview3DStore from '../store';
import { loadProductWithCodeQuery } from './__generated__/loadProductWithCodeQuery.graphql';
import { loadProductWithProductQuery } from './__generated__/loadProductWithProductQuery.graphql';

const LoadProductWithId = ({ productId, children }: { productId: string; children: React.ReactNode }) => {
    const [saveVersion, zkProduct, setZkProduct] = usePreview3DStore((store) => [store.saveVersion, store.zkProduct, store.setZkProduct]);

    const data = useLazyLoadQuery<loadProductWithProductQuery>(
        graphql`
            query loadProductWithProductQuery($productId: ID!) {
                model(id: $productId) {
                    id
                    modelID
                    statusID
                    name
                    code
                    providerCode
                    salesChannelID
                    sourceProductTemplateID
                    salesChannel {
                        ecommerceSettings {
                            ecommerceName
                        }
                    }
                    additionalCodes {
                        code
                        salesChannelID
                        merchantProductName
                        salesChannel {
                            ecommerceSettings {
                                ecommerceName
                                remoteUrl
                            }
                        }
                    }

                    modelColorMappings {
                        colorID
                        merchantCode
                        salesChannelID
                    }

                    pricingModelID
                    largeImageUrl
                    smallImageUrl
                    provider
                    environment
                    options {
                        optionID
                        colorID
                        sideID
                        optionsData {
                            isResizable
                            is3DAutoGenEnabled
                            isSplitOutput
                            startWidth
                            minWidth
                            maxWidth
                            stepWidth
                            startHeight
                            minHeight
                            maxHeight
                            stepHeight
                            depth
                            keepAspectRatio
                            lockWidth
                            lockHeight
                            showWidthSize
                            showHeightSize
                            showSplitGrid
                            mirrorType
                            edgesColor
                            minSplitWidth
                            minSplitHeight
                            startSplitColumns
                            startSplitRows
                            presets {
                                name
                                width
                                height
                            }
                        }
                    }
                    printTypes {
                        printTypeID
                        name
                        dpi
                        patID
                        outputTypeID
                        patID
                        ...modelPrintTypeRestrictionsFragment @relay(mask: false)

                        printTypeRestrictionsID
                        printTypeRestrictions {
                            ...partPrintTypeRestrictionsFragment @relay(mask: false)
                        }
                    }
                    colors {
                        colorID
                        colorGuid
                        name
                        code
                        colorProviderCode
                        position
                        sides {
                            sideID
                            sideGuid
                            name
                            tempImageFileKey
                            imageUrl
                            ppcm
                            code
                            sortOrder
                            mandatory
                            canRotate
                            cameraLocationID
                            areas {
                                areaID
                                boundsWidth
                                boundsHeight
                                boundsX
                                boundsY
                                path
                                pathSVG
                                name
                                clipOut
                                guid
                            }
                        }
                    }
                    scenes {
                        sceneID
                        isDefault
                        meshSides {
                            meshID
                            sideID
                            colorID
                            matrix
                            materialID
                            config {
                                general {
                                    desktopTextureResolutionWidth
                                    desktopTextureResolutionHeight
                                    mobileTextureResolutionWidth
                                    mobileTextureResolutionHeight
                                }
                            }
                            transform {
                                positionX
                                positionY
                                scalingX
                                scalingY
                                rotation
                            }
                        }
                    }
                    preview3D {
                        variantsAttributeOptions {
                            attributeGuid
                            variableMaterials {
                                materialID
                                meshes
                            }
                            variantOptions {
                                variantID
                                optionGuid
                                meshes
                                materials {
                                    optionMaterialID
                                    variableMaterialID
                                    actions {
                                        actionGuid
                                        meshID
                                    }
                                }
                            }
                        }
                    }
                    attributes {
                        attributeID
                        attributeGuid
                        attributeCode
                        attributeTypeID
                        defaultValue
                        displayOrder
                        name
                        description
                        groupID
                        groupGuid
                        stepID
                        stepGuid
                        optionPreviewShapeID
                        constraintExpression {
                            propositions {
                                attributeGuid
                                op
                                value
                            }
                            op
                        }
                        options {
                            optionID
                            optionGuid
                            optionCode
                            tempImageFileKey
                            imageUrl
                            tempImageFileObjectID
                            imageFileObjectID
                            name
                            description
                            displayOrder
                            isDefault
                            actions {
                                actionID
                                actionGuid
                                name
                                actionTypeID
                                sceneElementTypeID
                                selector
                                propertyName
                                value
                                execOrder
                                deviceTypes
                            }
                        }
                    }
                    attributeSteps {
                        stepID
                        stepGuid
                        groupID
                        groupGuid
                        name
                        displayOrder
                        cameraLocationID
                    }
                    attributeGroups {
                        groupID
                        groupGuid
                        tempImageFileKey
                        tempImageUrl
                        imageUrl
                        name
                        displayOrder
                        sceneCameraLocationID
                        meshSelector
                        direction
                        attributesAlwaysOpened
                    }
                }
            }
        `,
        {
            productId: productId
        },
        { fetchPolicy: 'network-only', fetchKey: saveVersion }
    );

    useEffect(() => {
        // Clone the data to be not read only because we need to merge the model when closing the dialogs
        const dataClone = JSON.parse(JSON.stringify(data.model));
        const newZkProduct = new MPlaza.Model(dataClone);

        // For DAM products there are no variants, but they are required for the code
        if (newZkProduct.get('colors').length === 0) {
            const newModelColor = new MPlaza.Color();
            newModelColor.set('colorID', 1);
            newZkProduct.get('colors').add(newModelColor);
        }

        setZkProduct(newZkProduct);
    }, [data.model, saveVersion]);

    // Wait for the product to be loaded
    if (!zkProduct) return null;

    return children;
};

export const LoadProductWithCode = ({ productCode, children }: { productCode: string; children: React.ReactNode }) => {
    const query = useLazyLoadQuery<loadProductWithCodeQuery>(
        graphql`
            query loadProductWithCodeQuery($productCode: String!, $salesChannelId: String!) {
                modelIDByCode(code: $productCode, salesChannelId: $salesChannelId)
            }
        `,
        {
            productCode,
            salesChannelId: toGlobalID(1, 'SalesChannel')
        }
    );

    return <LoadProductWithId productId={query.modelIDByCode}>{children}</LoadProductWithId>;
};

export const LoadProduct = ({ productId, productCode, children }: { productId?: string | null; productCode?: string | null; children: React.ReactNode }) => {
    return productCode ? (
        <LoadProductWithCode productCode={productCode}>{children}</LoadProductWithCode>
    ) : (
        <LoadProductWithId productId={productId!}>{children}</LoadProductWithId>
    );
};
